// Publish/subscribe policy (F-CALL-1): simulcast on, adaptive streaming on
// (off-screen tiles drop to low layer / pause — pairs with §9.3.6), dynacast
// so unsubscribed layers are not encoded.

import { AudioPreset, AudioPresets, RoomOptions, VideoPresets } from 'livekit-client';

// Voice-resilience profile for E2EE meetings. Insertable-stream E2EE forces the
// SDK to disable Opus RED (redundant packets can't survive per-frame
// encryption + SFU repacketization), so audio has no packet-level redundancy —
// a single lost packet is an audible gap, exactly what a non-E2EE path (Google
// Meet) hides with RED + SFU-side recovery. The one recovery that DOES survive
// E2EE is Opus in-band FEC (a low-bitrate copy of the previous frame carried
// inside each encrypted Opus payload). FEC only works across CONSECUTIVE frames
// and needs headroom, so we (a) turn DTX OFF — a continuous packet stream keeps
// FEC chains intact and the jitter buffer / PLC warm, and avoids clipping the
// onset of a word after silence — and (b) give the encoder a bit more bitrate.
const VOICE_RESILIENT: AudioPreset = { maxBitrate: 64_000 };

/** Org audio policy (from the admission grant). */
export interface AudioConfig {
  stereo: boolean;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
}

// Today's safe default: mono with the WebRTC processing on.
const DEFAULT_AUDIO: AudioConfig = {
  stereo: false,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

export function defaultRoomOptions(audio: AudioConfig = DEFAULT_AUDIO): RoomOptions {
  // Stereo hi-fi is a distinct MODE, not an add-on: the browser's audio
  // processing pipeline is mono, so any of echo/noise/gain being on forces a
  // mono downmix. Stereo therefore overrides all three to OFF; they apply only
  // in mono mode.
  const stereo = audio.stereo;
  return {
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: {
      simulcast: true,
      videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360],
      // Screen share is published NON-simulcast, as a single adaptive layer —
      // configured per-publish in setScreenShareEnabled (room.ts). A two-layer
      // screen share had a bandwidth dead zone that collapsed it below HD.
      audioPreset: stereo ? AudioPresets.musicHighQualityStereo : VOICE_RESILIENT,
      // DTX OFF everywhere: a continuous packet stream makes Opus in-band FEC
      // effective and keeps PLC / the jitter buffer warm under loss (E2EE
      // already removed RED — see VOICE_RESILIENT). Opus FEC itself is enabled
      // by the browser's encoder (useinbandfec=1) and adapts to reported loss;
      // there is no LiveKit publish flag for it.
      dtx: false,
    },
    videoCaptureDefaults: {
      resolution: VideoPresets.h720.resolution,
    },
    audioCaptureDefaults: {
      channelCount: stereo ? 2 : 1,
      echoCancellation: stereo ? false : audio.echoCancellation,
      noiseSuppression: stereo ? false : audio.noiseSuppression,
      autoGainControl: stereo ? false : audio.autoGainControl,
    },
  };
}
